

import { useState, useEffect, useCallback } from 'react';
import { projectRepository } from '@/features/projects/repository';
import { ProjectWithZones } from '@/features/projects/types';

/**
 * プロジェクト取得パラメータ
 */
interface GetProjectsParams {
  skip?: number;
  limit?: number;
  status?: string | string[];
  name?: string;
  address?: string;
  building_type?: string;
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  area_min?: number;
  area_max?: number;
  include_zones?: boolean;
}

/**
 * プロジェクトデータを管理するフック (FastAPI経由)
 * @param params パラメータ
 * @param options オプション
 * @returns プロジェクトデータ
 */
export const useProjects = (params: GetProjectsParams = {}) => {
  const [projects, setProjects] = useState<ProjectWithZones[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // paramsを安定化するため、JSON文字列で依存関係を管理
  const paramsKey = JSON.stringify(params);

  // データを取得する関数
  const fetchProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const apiParams = {
        tenant_id: undefined, // バックエンドで認証されたユーザーのテナントIDを使用
        status: Array.isArray(params.status) ? params.status : params.status,
        building_type: params.building_type,
        name: params.name,
        start_date_from: params.start_date_from,
        start_date_to: params.start_date_to,
        end_date_from: params.end_date_from,
        end_date_to: params.end_date_to,
        area_min: params.area_min,
        area_max: params.area_max,
        limit: params.limit,
        skip: params.skip,
        include_zones: params.include_zones,
      };

      const { items, total } = await projectRepository.getProjects(apiParams);
      setProjects(items);
      setTotal(total);
    } catch (err: unknown) {
      console.error('Failed to fetch projects:', err);
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : 'プロジェクトデータの取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [params.area_max, params.area_min, params.building_type, params.end_date_from, params.end_date_to, params.limit, params.name, params.skip, params.start_date_from, params.start_date_to, params.status]); // すべての依存関係を明示的に指定

  // 初回読み込み
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects, paramsKey]); // paramsKeyを依存関係に追加

  return {
    projects: projects || [],
    total,
    isLoading,
    isError: !!error,
    error,

    cacheKey: `api-projects-${paramsKey}`,
  };
};
